import apiClient from './apiClient';
import { normalizePackPurchaseResponse, type RawPackPurchaseResponse } from './purchaseResponse';
import { formatValidityHours } from '../../utils/packValidity';
import type { 
  ActivePack,
  PackCatalog,
  PackTemplate,
  PackTemplateFilters,
  PackDashboardResponse,
  PackHistoryKindFilter,
  PackHistoryResponse,
  PackHistoryStatusFilter,
  PackPurchaseRequest,
  PackPurchaseResponse,
  ScooterPreferences
} from './types';

class PackService {
  /**
   * Get active pack for a specific scooter
   */
  async getActivePack(scooterVin: string): Promise<ActivePack | null> {
    console.log('📦 PackService: Getting active pack for VIN:', scooterVin);

    try {
      const response = await apiClient.get<any>(
        `/api/v1/packs/active/${scooterVin}`
      );

      console.log('📦 PackService: Active pack API response:', response.data);

      // If API returns null, that means no active pack (valid state)
      if (response.data === null) {
        console.log('📦 PackService: No active pack (API returned null)');
        return null;
      }

      // Transform API response to frontend format
      const transformedPack = this.transformApiPackToFrontend(response.data);
      console.log('📦 PackService: Transformed pack:', transformedPack);

      const enhancedPack = this.enhancePackForUI(transformedPack);
      console.log('📦 PackService: Enhanced pack for UI:', enhancedPack);

      return enhancedPack;
    } catch (error: any) {
      // 404 means no active pack, which is a valid state (legacy handling)
      if (error.response?.status === 404) {
        console.log('📦 PackService: No active pack found (404)');
        return null;
      }

      // Re-throw other errors
      console.error('❌ PackService: Error getting active pack:', error);
      throw error;
    }
  }

  /**
   * Get unified rider pack history for energy and scooter packs.
   */
  async getPackHistory(filters?: {
    scooterVin?: string;
    kind?: PackHistoryKindFilter;
    status?: PackHistoryStatusFilter;
    limit?: number;
    offset?: number;
  }): Promise<PackHistoryResponse> {
    const params = new URLSearchParams();

    if (filters?.scooterVin) params.append('scooter_vin', filters.scooterVin);
    if (filters?.kind) params.append('kind', filters.kind);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));

    const queryString = params.toString();
    const response = await apiClient.get<PackHistoryResponse>(
      `/api/v1/packs/history${queryString ? `?${queryString}` : ''}`
    );

    return response.data;
  }

  /**
   * Get the normalized rider dashboard pack/readiness summary.
   */
  async getDashboardPackSummary(scooterVin: string): Promise<PackDashboardResponse> {
    const response = await apiClient.get<PackDashboardResponse>(
      `/api/v1/packs/dashboard/${scooterVin}`
    );

    return response.data;
  }

  /**
   * Get all packs for a user's scooter (active, queued, expired)
   */
  async getScooterPacks(scooterVin: string): Promise<ActivePack[]> {
    const response = await apiClient.get<{ packs: any[] }>(
      `/api/v1/packs?scooter_vin=${scooterVin}`
    );
    
    // Transform each pack from API format to frontend format
    return response.data.packs.map((apiPack: any) => {
      const transformedPack = this.transformApiPackToFrontend(apiPack);
      return this.enhancePackForUI(transformedPack);
    });
  }

  /**
   * Get queued packs for a scooter (for stack visualization)
   */
  async getQueuedPacks(scooterVin: string): Promise<ActivePack[]> {
    const allPacks = await this.getScooterPacks(scooterVin);
    // API returns 'reserved' for queued packs
    return allPacks
      .filter(pack => pack.status === 'reserved' || pack.status === 'queued')
      .sort((a: ActivePack, b: ActivePack) => {
        // Sort by creation date if no priority is set
        if (!a.priority_in_queue && !b.priority_in_queue) {
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        }
        return (a.priority_in_queue || 0) - (b.priority_in_queue || 0);
      });
  }

  /**
   * Get all pack data for a scooter in a single API call
   * Returns active pack and queued packs separately
   */
  async getScooterPackData(scooterVin: string): Promise<{
    activePack: ActivePack | null;
    queuedPacks: ActivePack[];
    allPacks: ActivePack[];
  }> {
    console.log('📦 PackService: Getting all pack data for VIN:', scooterVin);
    
    const [activePack, allPacks] = await Promise.all([
      this.getActivePack(scooterVin),
      this.getScooterPacks(scooterVin),
    ]);
    
    // Filter and sort queued packs
    const queuedPacks = allPacks
      .filter(pack => pack.status === 'reserved' || pack.status === 'queued')
      .sort((a: ActivePack, b: ActivePack) => {
        if (!a.priority_in_queue && !b.priority_in_queue) {
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        }
        return (a.priority_in_queue || 0) - (b.priority_in_queue || 0);
      });
    
    console.log('📦 PackService: Pack data summary:', {
      total: allPacks.length,
      active: activePack ? 1 : 0,
      queued: queuedPacks.length
    });
    
    const activePackAlreadyListed = activePack
      ? allPacks.some(pack => pack.pack_id === activePack.pack_id)
      : false;

    return {
      activePack,
      queuedPacks,
      allPacks: activePack && !activePackAlreadyListed
        ? [activePack, ...allPacks]
        : allPacks
    };
  }

  /** Map one API template row onto the client model. */
  private toPackTemplate(item: any, scooterVin?: string): PackTemplate {
    const validityLabel = formatValidityHours(item.validity_hours);
    return {
      id: item.template_id,
      name: item.template_name,
      description: `${item.pack_type} pack with ${item.energy_amount_ah} Ah${validityLabel ? ` and ${validityLabel} validity` : ''}`,
      pack_type: item.pack_type as 'hybrid' | 'energy_only' | 'time_based', // Use actual pack type from API
      price_npr: parseInt(item.price_npr),
      ah_limit: parseFloat(item.energy_amount_ah),
      validity_hours: item.validity_hours || undefined,
      is_active: item.is_active,
      is_featured: false, // Not in API response, default to false
      display_order: item.priority || 1,
      recommended_for_scooter: scooterVin,
      ineligible_code: item.ineligible_code,
      ineligible_reason: item.ineligible_reason,
    };
  }

  /**
   * Get the catalog for one scooter: the packs that can be bought, plus the
   * packs the API returned as visible-but-blocked with the reason why.
   *
   * `ineligible_templates` is absent on older backends, which simply yields an
   * empty `blocked` list and the previous behaviour.
   */
  async getPackCatalog(scooterVin: string): Promise<PackCatalog> {
    const params = new URLSearchParams({ is_active: 'true', scooter_vin: scooterVin });
    const response = await apiClient.get<{
      templates: any[];
      ineligible_templates?: any[];
      user_eligibility_info?: { catalog_notice?: string | null };
    }>(`/api/v1/packs/templates?${params.toString()}`);

    const byOrder = (a: PackTemplate, b: PackTemplate) =>
      a.display_order - b.display_order;
    return {
      templates: (response.data?.templates || [])
        .map((item: any) => this.toPackTemplate(item, scooterVin))
        .sort(byOrder),
      blocked: (response.data?.ineligible_templates || [])
        .map((item: any) => this.toPackTemplate(item, scooterVin))
        .sort(byOrder),
      notice: response.data?.user_eligibility_info?.catalog_notice ?? null,
    };
  }

  /**
   * Get available pack templates (fetching REAL data from API)
   */
  async getPackTemplates(filters?: PackTemplateFilters): Promise<PackTemplate[]> {
    console.log('📦 PackService: Getting pack templates with filters:', filters);
    
    try {
      // Try to fetch from the actual admin API endpoint
      const params = new URLSearchParams();
      
      if (filters?.pack_type) params.append('pack_type', filters.pack_type);
      if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
      if (filters?.is_featured !== undefined) params.append('is_featured', String(filters.is_featured));
      if (filters?.max_price) params.append('max_price', String(filters.max_price));
      if (filters?.scooter_vin) params.append('scooter_vin', filters.scooter_vin);
      
      // Use the user endpoint to get eligible templates
      const response = await apiClient.get<{
        templates: any[];
        ineligible_templates?: any[];
        user_eligibility_info: any;
      }>(
        `/api/v1/packs/templates?${params.toString()}`
      );
      
      console.log('📦 PackService: Raw API response:', response.data);
      
      // The user endpoint returns {templates: [...], user_eligibility_info: {...}}
      const templatesData = response.data?.templates || [];
      console.log('📦 PackService: User eligibility info:', response.data?.user_eligibility_info);
      
      let templates: PackTemplate[] = templatesData.map((item: any) =>
        this.toPackTemplate(item, filters?.scooter_vin),
      );
      
      console.log('📦 PackService: Converted templates:', templates);
      
      // Apply frontend filters if not already applied by API
      if (filters?.is_active !== undefined) {
        templates = templates.filter(t => t.is_active === filters.is_active);
        console.log(`📦 After is_active=${filters.is_active} filter:`, templates.length, 'templates');
      }
      
      if (filters?.is_featured !== undefined) {
        templates = templates.filter(t => t.is_featured === filters.is_featured);
      }
      
      if (filters?.max_price) {
        templates = templates.filter(t => t.price_npr <= filters.max_price!);
      }
      
      console.log('📦 PackService: Final filtered templates:', templates);
      return templates.sort((a, b) => a.display_order - b.display_order);
      
    } catch (error) {
      console.error('❌ PackService: Failed to fetch templates from API:', error);
      
      // Fallback: return empty array instead of mock data
      console.log('📦 PackService: Returning empty array due to API error');
      return [];
    }
  }

  /**
   * Get featured pack templates for a specific scooter
   */
  async getFeaturedPacks(scooterVin: string, limit: number = 3): Promise<PackTemplate[]> {
    console.log('📦 getFeaturedPacks called for VIN:', scooterVin, 'with limit:', limit);

    const allTemplates = await this.getPackTemplates({
      is_active: true,
      scooter_vin: scooterVin,
    });

    console.log('📦 getFeaturedPacks: Retrieved', allTemplates.length, 'templates');

    // Return top N templates (already sorted by display_order)
    const result = allTemplates.slice(0, limit);
    console.log('📦 getFeaturedPacks: Returning', result.length, 'featured packs');

    return result;
  }

  /**
   * Purchase a pack
   */
  async purchasePack(request: PackPurchaseRequest): Promise<PackPurchaseResponse> {
    console.log('📦 PackService: Purchasing pack with request:', request);
    
    try {
      const response = await apiClient.post<RawPackPurchaseResponse>(
        '/api/v1/packs/purchase',
        request
      );
      
      console.log('📦 PackService: Purchase response:', response.data);
      
      return normalizePackPurchaseResponse(response.data);
    } catch (error: any) {
      console.error('📦 PackService: Purchase error:', error);
      console.error('📦 PackService: Error response:', error.response?.data);
      
      // FastAPI returns errors in 'detail' field
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      // Fallback to generic error message
      throw new Error(error.message || 'Failed to purchase pack');
    }
  }

  /**
   * Check if user can afford a pack
   */
  async checkPackAffordability(
    templateId: string, 
    currentWalletBalance: number
  ): Promise<{
    can_afford: boolean;
    pack_price: number;
    amount_needed: number;
  }> {
    const templates = await this.getPackTemplates();
    const template = templates.find(t => t.id === templateId);
    
    if (!template) {
      throw new Error('Pack template not found');
    }
    
    const can_afford = currentWalletBalance >= template.price_npr;
    const amount_needed = Math.max(0, template.price_npr - currentWalletBalance);
    
    return {
      can_afford,
      pack_price: template.price_npr,
      amount_needed,
    };
  }

  /**
   * Get pack debt for a scooter (if blacklisted due to usage)
   */
  async getPackDebt(scooterVin: string): Promise<{
    has_debt: boolean;
    has_energy_debt: boolean;
    has_scooter_pack_debt: boolean;
    energy_debt_ah: number;
    scooter_debt_km: number;
    status: string;
  }> {
    const response = await apiClient.get<any>(
      `/api/v1/packs/debt/${scooterVin}`
    );

    const data = response.data;
    const debtEnergyAh = parseFloat(data.energy_debt_ah ?? '0');
    const debtKm = parseFloat(data.scooter_debt_km ?? '0');
    const hasPendingDebt = data.status === 'pending';

    return {
      has_debt: hasPendingDebt && (debtEnergyAh > 0 || debtKm > 0),
      has_energy_debt: hasPendingDebt && debtEnergyAh > 0,
      has_scooter_pack_debt: hasPendingDebt && debtKm > 0,
      energy_debt_ah: debtEnergyAh,
      scooter_debt_km: debtKm,
      status: data.status || 'clear',
    };
  }

  /**
   * Transform API pack response to frontend format
   */
  private transformApiPackToFrontend(apiPack: any): ActivePack {
    // Calculate remaining values
    const energyAllocationAh = parseFloat(apiPack.energy_allocation_ah || '0');
    const consumedEnergyAh = parseFloat(apiPack.energy_consumed_ah || '0');
    const ahRemaining = energyAllocationAh - consumedEnergyAh;
    
    // Calculate time remaining if validity_hours exists
    let hoursRemaining = 0;
    let hoursUsed = 0;
    if (apiPack.validity_hours && apiPack.activation_date) {
      const activationDate = new Date(apiPack.activation_date);
      const now = new Date();
      const hoursElapsed = (now.getTime() - activationDate.getTime()) / (1000 * 60 * 60);
      hoursUsed = Math.round(hoursElapsed);
      hoursRemaining = Math.max(0, apiPack.validity_hours - hoursUsed);
    }
    
    // Use template name from API response, fallback to pack type if not available
    const templateName = apiPack.template_name || 
                       (apiPack.pack_type === 'hybrid' ? 'Hybrid Pack' :
                        apiPack.pack_type === 'energy_only' ? 'Energy Pack' :
                        'Time Based Pack');
    
    return {
      pack_id: apiPack.pack_id,
      template_id: apiPack.template_id,
      template_name: templateName,
      pack_type: apiPack.pack_type,
      scooter_vin: apiPack.scooter_vin,
      status: apiPack.status,
      
      // Pack limits and usage
      ah_limit: energyAllocationAh > 0 ? energyAllocationAh : undefined,
      ah_used: consumedEnergyAh,
      ah_remaining: ahRemaining,
      
      validity_hours: apiPack.validity_hours || undefined,
      hours_used: hoursUsed,
      hours_remaining: hoursRemaining,
      
      // Dates
      created_at: apiPack.created_at,
      activated_at: apiPack.activation_date,
      expires_at: apiPack.expiry_date,
      
      // Queue information (if applicable)
      priority_in_queue: apiPack.priority_in_queue,
      
      // Designation status
      designated_next: apiPack.designated_next || false,
      
      // UI enhancements will be added by enhancePackForUI
      status_color: 'green', // Default, will be updated
    };
  }

  /**
   * Enhance pack with UI-specific properties
   */
  private enhancePackForUI(pack: ActivePack): ActivePack {
    // Determine status color based on remaining usage
    let status_color: 'green' | 'orange' | 'red' | 'grey' | 'black' = 'green';
    
    switch (pack.status) {
      case 'active':
        // Check remaining usage levels
        const ahPercentage = pack.ah_limit ? (pack.ah_remaining / pack.ah_limit) * 100 : 100;
        const hoursPercentage = pack.validity_hours ? (pack.hours_remaining / pack.validity_hours) * 100 : 100;
        const minPercentage = Math.min(ahPercentage, hoursPercentage);
        
        if (minPercentage > 50) status_color = 'green';
        else if (minPercentage > 20) status_color = 'orange';
        else status_color = 'red';
        break;
      
      case 'queued':
      case 'reserved':
        status_color = 'grey';
        break;
      
      case 'expired':
      case 'consumed':
      case 'depleted':
        status_color = 'black';
        break;
    }
    
    return {
      ...pack,
      status_color,
    };
  }

  /**
   * Calculate estimated pack duration based on usage history
   */
  async getPackDurationEstimate(
    scooterVin: string, 
    templateId: string
  ): Promise<{
    estimated_days: number;
    confidence: 'high' | 'medium' | 'low';
    based_on: string;
  }> {
    const response = await apiClient.get<any>(
      `/api/v1/packs/duration-estimate?scooter_vin=${scooterVin}&template_id=${templateId}`
    );
    
    // Direct response from backend
    return response.data;
  }
  /**
   * Get scooter preferences including auto-activation settings
   */
  async getScooterPreferences(scooterVin: string): Promise<ScooterPreferences> {
    const response = await apiClient.get<ScooterPreferences>(
      `/api/v1/packs/preferences/${scooterVin}`
    );
    return response.data;
  }

  /**
   * Update scooter preferences
   */
  async updateScooterPreferences(
    scooterVin: string, 
    preferences: Partial<ScooterPreferences>
  ): Promise<ScooterPreferences> {
    const response = await apiClient.put<ScooterPreferences>(
      `/api/v1/packs/preferences/${scooterVin}`,
      preferences
    );
    return response.data;
  }

  /**
   * Manually activate a reserved energy pack.
   */
  async activatePack(packId: string): Promise<ActivePack> {
    console.log('PackService: Activating pack:', packId);

    try {
      const response = await apiClient.post<any>(
        `/api/v1/packs/${packId}/activate`
      );
      console.log('PackService: Activation response:', response.data);
      return this.enhancePackForUI(this.transformApiPackToFrontend(response.data));
    } catch (error: any) {
      console.error('PackService: Activation failed:', error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  /**
   * Designate a pack as next to activate
   */
  async designateNextPack(packId: string): Promise<ActivePack> {
    const response = await apiClient.put<any>(
      `/api/v1/packs/${packId}/designate-next`
    );
    return this.transformApiPackToFrontend(response.data);
  }

  /**
   * Clear next designation from a pack
   */
  async clearPackDesignation(packId: string): Promise<ActivePack> {
    const response = await apiClient.delete<any>(
      `/api/v1/packs/${packId}/designate-next`
    );
    return this.transformApiPackToFrontend(response.data);
  }
}

export const packService = new PackService();
export default packService;
